import { BlogPost } from "../../../mocks/blog";
import { useNavigate } from "react-router-dom";

const categoryColor: Record<string, { text: string; bg: string; border: string }> = {
  "비즈니스 전략": { text: "text-brand-purple", bg: "bg-brand-purple/10", border: "border-brand-purple/30" },
  "수익화": { text: "text-brand-teal", bg: "bg-brand-teal/10", border: "border-brand-teal/30" },
  "마케팅": { text: "text-brand-orange", bg: "bg-brand-orange/10", border: "border-brand-orange/30" },
  "법무·세금": { text: "text-yellow-400", bg: "bg-yellow-400/10", border: "border-yellow-400/30" },
  "성공 사례": { text: "text-pink-400", bg: "bg-pink-400/10", border: "border-pink-400/30" },
};

interface BlogCardProps {
  post: BlogPost;
  variant?: "featured" | "normal";
}

export default function BlogCard({ post, variant = "normal" }: BlogCardProps) {
  const navigate = useNavigate();
  const color = categoryColor[post.category] ?? { text: "text-white/60", bg: "bg-white/10", border: "border-white/20" };

  const handleClick = () => navigate(`/blog/${post.slug}`);

  if (variant === "featured") {
    return (
      <article
        onClick={handleClick}
        className="group glass rounded-3xl overflow-hidden cursor-pointer transition-all duration-300 hover:border-brand-purple/30 grid grid-cols-1 lg:grid-cols-2"
        style={{ border: "1px solid rgba(255,255,255,0.07)" }}
      >
        <div className="relative overflow-hidden h-64 lg:h-full min-h-[280px]">
          <img
            src={post.coverImage}
            alt={post.title}
            className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(168,85,247,0.2), rgba(0,0,0,0.3))" }} />
          <span className="absolute top-5 left-5 text-xs font-display font-semibold tracking-wider px-3 py-1 rounded-full border backdrop-blur-sm bg-black/40 text-white border-white/20">
            FEATURED
          </span>
        </div>
        <div className="p-7 lg:p-10 flex flex-col justify-center gap-5">
          <span className={`inline-flex w-fit text-xs font-display font-semibold tracking-widest uppercase px-3 py-1 rounded-full border ${color.text} ${color.bg} ${color.border}`}>
            {post.category}
          </span>
          <h2 className="font-display text-xl md:text-2xl lg:text-3xl font-bold text-white leading-snug lg:leading-relaxed group-hover:text-brand-purple transition-colors duration-300">
            {post.title}
          </h2>
          <p className="text-white/50 text-sm font-body leading-loose line-clamp-3">{post.excerpt}</p>
          <div className="flex items-center justify-between pt-2 border-t border-white/5">
            <div className="flex items-center gap-3">
              <img src={post.author.avatar} alt={post.author.name} className="w-8 h-8 rounded-full object-cover object-top" />
              <div>
                <p className="text-xs text-white font-display font-medium">{post.author.name}</p>
                <p className="text-xs text-white/40 font-body">{post.author.role}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-white/30 text-xs font-body">
              <span>{post.publishedAt.replace(/-/g, ".")}</span>
              <span>·</span>
              <span>{post.readTime}분 읽기</span>
            </div>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article
      onClick={handleClick}
      className="group glass rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:border-brand-purple/25 flex flex-col"
      style={{ border: "1px solid rgba(255,255,255,0.07)" }}
    >
      <div className="relative overflow-hidden h-48">
        <img
          src={post.coverImage}
          alt={post.title}
          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(6,8,15,0.6), transparent)" }} />
      </div>
      <div className="p-5 lg:p-6 flex flex-col gap-4 flex-1">
        <span className={`inline-flex w-fit text-xs font-display font-semibold tracking-widest uppercase px-2.5 py-1 rounded-full border ${color.text} ${color.bg} ${color.border}`}>
          {post.category}
        </span>
        <h3 className="font-display text-base lg:text-lg font-bold text-white leading-snug lg:leading-relaxed group-hover:text-brand-purple transition-colors duration-300 line-clamp-2">
          {post.title}
        </h3>
        <p className="text-white/40 text-xs font-body leading-loose line-clamp-2 flex-1">{post.excerpt}</p>
        <div className="flex items-center justify-between pt-3 border-t border-white/5">
          <div className="flex items-center gap-2">
            <img src={post.author.avatar} alt={post.author.name} className="w-6 h-6 rounded-full object-cover object-top" />
            <span className="text-xs text-white/50 font-body whitespace-nowrap">{post.author.name}</span>
          </div>
          <span className="text-xs text-white/30 font-body whitespace-nowrap">{post.readTime}분 읽기</span>
        </div>
      </div>
    </article>
  );
}
